import { useCallback, useEffect, useRef } from 'react';

import { dispatchTrackingEventGA4 } from 'common/tracking/dispatchTrackingEventGA4';
import type { CustomFilter } from 'common/types/SortAndFilter';
import { Element } from 'common/types/tracking';
import type { FilterGroup } from 'common/utils/filterGroups';
import {
  Label,
  trackCategoryButtonOpenEvent,
} from '../../../category-options-buttons/components/hooks';
import type { EventType } from '../enums';

const DEBOUNCE_TIMEOUT = 300;

enum EventName {
  PromotedFilterView = 'promoted-filters_view',
  PromotedFilterClick = 'promoted-filters_click',
}

interface TrackingOptions {
  initiallyExpanded: string[];
  label: string;
  listPosition: number;
  element: Element;
  eventType: EventType;
}

interface TrackedItem {
  key: string;
  eventType: EventType;
}

export const trackCarouselItemsView = (
  displayedItems: TrackedItem[],
  allItems: Array<FilterGroup | CustomFilter>
) => {
  dispatchTrackingEventGA4({
    event: EventName.PromotedFilterView,
    list_length: allItems.length,
    items: [
      ...displayedItems.map(trackedItem => {
        const filter = allItems.find(({ key }) => key === trackedItem.key);

        return {
          list_position: allItems.findIndex(({ key }) => key === trackedItem.key),
          list_item: filter ? filter.name : '',
          type: trackedItem.eventType,
        };
      }),
    ],
  });
};

export const useFiltersViewTracking = (items: Array<FilterGroup | CustomFilter>) => {
  const displayedItemsRef = useRef<TrackedItem[]>([]);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(null);
  const itemsRef = useRef(items);
  itemsRef.current = items;

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const handleObserve = useCallback((item: FilterGroup | CustomFilter, eventType: EventType) => {
    displayedItemsRef.current.push({ key: item.key, eventType });

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      trackCarouselItemsView(displayedItemsRef.current, itemsRef.current);
      displayedItemsRef.current = [];
    }, DEBOUNCE_TIMEOUT);
  }, []);

  return { handleObserve };
};

interface UsePromotedFiltersActionsProps {
  filters: FilterGroup[];
  customFilters: CustomFilter[];
  onOpenDrawer: (initiallyExpanded: string[]) => void;
}

export const usePromotedFiltersActions = ({
  filters,
  customFilters,
  onOpenDrawer,
}: UsePromotedFiltersActionsProps) => {
  const allPromotedItems = [...customFilters, ...filters];
  const { handleObserve } = useFiltersViewTracking(allPromotedItems);

  const handleChipClick = ({
    initiallyExpanded,
    label,
    listPosition,
    element,
    eventType,
  }: TrackingOptions) => {
    if (element === Element.Button) {
      onOpenDrawer(initiallyExpanded);
    }

    dispatchTrackingEventGA4({
      event: EventName.PromotedFilterClick,
      element,
      list_length: allPromotedItems.length,
      list_position: listPosition,
      label,
      type: eventType,
    });
  };

  const handleFilterButtonClick = () => {
    onOpenDrawer(['promoted-filters']);
    trackCategoryButtonOpenEvent(Label.Filters);
  };

  return { handleObserve, handleChipClick, handleFilterButtonClick };
};
